/* =========================================================================
   LAZ Skincare — regimen client

   Shared by page.regimen.liquid and page.shared-regimen.liquid.

   Product markup comes from sections/regimen-row.liquid, fetched per product
   via /products/{handle}?section_id=regimen-row. That costs one request each
   but means prices, ratings and sale badges are rendered by the theme's own
   snippets and never drift when the theme updates.

   Step assignment: products may carry several values in custom.regimen_step;
   we always take the first. (Catalog is moving to one step per product.)
   ========================================================================= */

(function () {
  "use strict";

  const API = "/apps/regimen";

  /* Mirrors STEP_CHOICES in the Worker. Both derive from the product
     metafield definition custom.regimen_step — if a merchant edits those
     choices, update the Worker first, then this. */
  const STEP_CHOICES = [
    "1 - Cleansers",
    "2 - Exfoliants",
    "3 - Toners & Mists",
    "4 - Masks",
    "5 - Serums",
    "5b - Eye Care",
    "6 - Moisturizers",
    "7 - Sunscreen",
  ];
  const UNRANKED = 999;

  /* ---------- api ---------- */

  async function api(path, options = {}) {
    const res = await fetch(`${API}/${path}`, {
      ...options,
      headers: { "Content-Type": "application/json", ...(options.headers || {}) },
    });

    let body;
    try {
      body = await res.json();
    } catch {
      throw new Error("Unexpected response from the server.");
    }

    if (!res.ok) {
      const err = new Error(body.error || "Request failed");
      err.status = res.status;
      err.body = body;
      throw err;
    }
    return body;
  }

  const get = (path) => api(path, { method: "GET" });
  const post = (path, body) =>
    api(path, { method: "POST", body: JSON.stringify(body) });

  /* ---------- product row fetching ---------- */

  const rowCache = new Map();

  async function fetchRow(handle) {
    if (rowCache.has(handle)) return rowCache.get(handle);

    const promise = fetch(
      `/products/${encodeURIComponent(handle)}?section_id=regimen-row`
    )
      .then((res) => {
        if (!res.ok) throw new Error(`Row fetch failed for ${handle}`);
        return res.text();
      })
      .then((html) => {
        const doc = new DOMParser().parseFromString(html, "text/html");
        const inner = doc.querySelector(".regimen-row__inner");
        if (!inner) throw new Error(`No row markup for ${handle}`);
        return inner;
      });

    rowCache.set(handle, promise);
    return promise;
  }

  /* Fetches every row in parallel. A failed fetch resolves to null so one
     archived or renamed product cannot blank the whole page. */
  async function fetchRows(items) {
    const results = await Promise.allSettled(
      items.map((item) => (item.handle ? fetchRow(item.handle) : Promise.reject()))
    );

    return items.map((item, i) => ({
      item,
      node: results[i].status === "fulfilled" ? results[i].value.cloneNode(true) : null,
    }));
  }

  /* ---------- helpers ---------- */

  function firstStep(node) {
    if (!node) return null;
    try {
      const steps = JSON.parse(node.dataset.steps || "[]");
      const valid = steps.filter((s) => STEP_CHOICES.includes(s));
      return valid.length ? valid[0] : null;
    } catch {
      return null;
    }
  }

  function activesOf(node) {
    if (!node) return [];
    try {
      const parsed = JSON.parse(node.dataset.actives || "[]");
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  }

  function stepRank(label) {
    const idx = STEP_CHOICES.indexOf(label);
    return idx >= 0 ? idx : UNRANKED;
  }

  function groupByStep(entries) {
    const groups = new Map();
    for (const entry of entries) {
      const label = entry.item.step_label || "Other";
      if (!groups.has(label)) groups.set(label, []);
      groups.get(label).push(entry);
    }
    return [...groups.entries()].sort(
      (a, b) => stepRank(a[0]) - stepRank(b[0])
    );
  }

  function el(tag, className, text) {
    const node = document.createElement(tag);
    if (className) node.className = className;
    if (text != null) node.textContent = text; // textContent, never innerHTML
    return node;
  }

  function toast(message, tone = "info") {
    const existing = document.querySelector(".regimen-toast");
    if (existing) existing.remove();

    const node = el("div", `regimen-toast regimen-toast--${tone}`, message);
    node.setAttribute("role", "status");
    document.body.appendChild(node);
    requestAnimationFrame(() => node.classList.add("is-visible"));

    setTimeout(() => {
      node.classList.remove("is-visible");
      setTimeout(() => node.remove(), 300);
    }, 4000);
  }

  /* ---------- interaction warnings ---------- */

  const ACTIVE_GROUPS = {
    "Glycolic Acid (AHA)": "aha",
    "Lactic Acid (AHA)": "aha",
    "Mandelic Acid (AHA)": "aha",
    "Salicylic Acid (BHA)": "bha",
    Retinoid: "retinoid",
    "Benzoyl Peroxide": "benzoyl_peroxide",
    "Vitamin C": "vitamin_c",
    "Azelaic Acid": "azelaic",
    "Kojic Acid": "kojic",
    Peptides: "peptides",
    Exosomes: "exosomes",
  };

  const SEVERITY_RANK = { caution: 0, sequence: 1, separate: 2 };

  const INTERACTIONS = [
    { pair: ["retinoid", "aha"], severity: "sequence", headline: "Alternate nights", detail: "Both speed cell turnover. Using them the same evening increases irritation without adding benefit." },
    { pair: ["retinoid", "bha"], severity: "sequence", headline: "Alternate nights", detail: "Salicylic acid and retinoids both exfoliate. Alternating keeps the barrier intact." },
    { pair: ["retinoid", "benzoyl_peroxide"], severity: "separate", headline: "Split between morning and evening", detail: "Benzoyl peroxide can oxidize certain retinoid formulations and reduce their potency." },
    { pair: ["retinoid", "vitamin_c"], severity: "separate", headline: "Vitamin C in the morning, retinoid at night", detail: "Vitamin C pairs naturally with daytime sun protection; retinoids are photosensitizing." },
    { pair: ["retinoid", "azelaic"], severity: "caution", headline: "Introduce one at a time", detail: "Often prescribed together, but add the second gradually and ease off if you see flaking." },
    { pair: ["retinoid", "kojic"], severity: "caution", headline: "Introduce one at a time", detail: "Both target pigmentation and both can irritate. Build up slowly." },
    { pair: ["retinoid", "exosomes"], severity: "caution", headline: "Check product directions", detail: "Exosome products are often intended for recovery nights rather than alongside actives." },
    { pair: ["aha", "bha"], severity: "caution", headline: "Consider alternating", detail: "Stacking separate acid products is where barrier damage tends to happen." },
    { pair: ["aha", "vitamin_c"], severity: "sequence", headline: "Use at different times", detail: "Both are low-pH and the combination commonly stings. Vitamin C morning, acids night." },
    { pair: ["bha", "vitamin_c"], severity: "caution", headline: "Space them apart", detail: "Better tolerated than AHA with vitamin C, but move them apart if you notice tightness." },
    { pair: ["aha", "benzoyl_peroxide"], severity: "sequence", headline: "Alternate rather than layering", detail: "Both are drying; together they often push skin past its tolerance." },
    { pair: ["bha", "benzoyl_peroxide"], severity: "sequence", headline: "Alternate rather than layering", detail: "A common acne pairing, but usually on alternating days." },
    { pair: ["aha", "azelaic"], severity: "caution", headline: "Introduce one at a time", detail: "Azelaic acid is gentle, but stacking onto an existing acid routine can tip sensitive skin over." },
    { pair: ["bha", "azelaic"], severity: "caution", headline: "Introduce one at a time", detail: "Frequently used together for acne. Build up gradually." },
    { pair: ["aha", "kojic"], severity: "caution", headline: "Watch for sensitivity", detail: "Kojic acid can sensitize on its own; exfoliation increases that risk." },
    { pair: ["aha", "peptides"], severity: "caution", headline: "Apply peptides after acids absorb", detail: "Some peptides are sensitive to low pH. Spacing them a few minutes apart costs nothing." },
    { pair: ["aha", "exosomes"], severity: "caution", headline: "Apply to calm skin", detail: "Exosome products are usually positioned for recovery, not fresh exfoliation." },
    { pair: ["benzoyl_peroxide", "vitamin_c"], severity: "separate", headline: "Split between morning and evening", detail: "Benzoyl peroxide oxidizes vitamin C. Separating them preserves both." },
    { pair: ["benzoyl_peroxide", "azelaic"], severity: "caution", headline: "Watch for dryness", detail: "Often layered successfully, but the combination is drying." },
    { pair: ["vitamin_c", "azelaic"], severity: "caution", headline: "Introduce gradually", detail: "Effective together for pigmentation, but two actives at once can be a lot for reactive skin." },
  ];

  const INTERACTION_MAP = new Map(
    INTERACTIONS.map((r) => [[...r.pair].sort().join("|"), r])
  );

  function groupsForActives(actives) {
    const groups = new Set();
    for (const name of actives) {
      const g = ACTIVE_GROUPS[name];
      if (g) groups.add(g);
    }
    return [...groups];
  }

  function scanInteractions(entries) {
    const findings = [];

    for (let i = 0; i < entries.length; i++) {
      for (let j = i + 1; j < entries.length; j++) {
        const a = groupsForActives(entries[i].actives);
        const b = groupsForActives(entries[j].actives);
        if (!a.length || !b.length) continue;

        let worst = null;
        for (const ga of a) {
          for (const gb of b) {
            if (ga === gb) continue;
            const rule = INTERACTION_MAP.get([ga, gb].sort().join("|"));
            if (!rule) continue;
            if (!worst || SEVERITY_RANK[rule.severity] > SEVERITY_RANK[worst.severity]) {
              worst = rule;
            }
          }
        }

        if (worst) {
          findings.push({
            titles: [entries[i].title, entries[j].title],
            severity: worst.severity,
            headline: worst.headline,
            detail: worst.detail,
          });
        }
      }
    }

    return findings.sort(
      (x, y) => SEVERITY_RANK[y.severity] - SEVERITY_RANK[x.severity]
    );
  }

  function renderInteractions(findings, container) {
    container.textContent = "";
    if (!findings.length) {
      container.hidden = true;
      return;
    }
    container.hidden = false;

    const heading = el("h2", "regimen-notes__heading heading text-xl", "Using these together");
    container.appendChild(heading);

    const list = el("ul", "regimen-notes__list");
    list.setAttribute("role", "list");

    for (const f of findings) {
      const li = el("li", `regimen-note regimen-note--${f.severity}`);
      li.appendChild(el("p", "regimen-note__headline font-medium", f.headline));
      li.appendChild(
        el("p", "regimen-note__products text-xs text-opacity", f.titles.join(" + "))
      );
      if (f.detail) {
        li.appendChild(el("p", "regimen-note__detail text-sm", f.detail));
      }
      list.appendChild(li);
    }

    container.appendChild(list);
  }

  /* ---------- exports ---------- */

  window.LazRegimen = {
    api: { get, post },
    fetchRows,
    firstStep,
    activesOf,
    stepRank,
    groupByStep,
    scanInteractions,
    renderInteractions,
    el,
    toast,
    STEP_CHOICES,
  };
})();